import { useEffect } from "react"

import { Button, Flex, Form, Radio, Tooltip, Typography } from "antd"
import cn from "classnames"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router"

import Icon from "../../Icon"
import { createAccountStepPath, onboardingStepPath, validateUrl } from "../../Utils/utils"
import ButtonComponent from "../Button"
import InputComponent from "../Input"
import PhoneInput from "../PhoneInput"

const { Text } = Typography

const options = [
  {
    value: true,
    title: "Yes",
    description: "I’m still using another provider"
  },
  {
    value: false,
    title: "No",
    description: "I’m not using another provider"
  }
]

function BusinessInfoForm({ form, onSubmit, onboarding, disabled }) {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isNewBusiness = pathname?.includes("/create-account")

  const isUseAnotherSolutions = Form.useWatch("is_use_another_solutions", form)
  const phone = Form.useWatch("phone", form)
  // Optional field: an incomplete number never blocks Continue, it is just left
  // out of the save (see OnboardingBusinessInfoPage handleSubmit).
  const isPhoneIncomplete = !!phone?.number?.trim() && !phone?.valid

  const handleNavigate = () => {
    if (isNewBusiness) {
      navigate(createAccountStepPath[7])
      return
    }
    navigate(onboardingStepPath[2])
  }

  const onFinish = (values) => {
    onSubmit(values)
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
  }

  function onFocusHandler(e) {
    const field = e.target.name || e.target.id
    handleClearError(e, field)
  }

  const addErrorIcon = (error) => (
    <span>
      <Icon type={"alert_triangle_error"} /> {t(error)}
    </span>
  )

  useEffect(() => {
    if (isUseAnotherSolutions) {
      form.resetFields(["is_user_notified_previous_solution"])
    }
  }, [isUseAnotherSolutions])

  return (
    <Form
      className="with-default-error ob-form"
      onFinish={onFinish}
      validateTrigger="onSubmit"
      layout="vertical"
      form={form}
      requiredMark={false}
      disabled={disabled}
    >
      <Form.Item
        name="name"
        style={{ padding: 0 }}
        label={t("Business name")}
        rules={[
          {
            required: true,
            message: "Required fields were not filled."
          }
        ]}
      >
        <InputComponent placeholder={t("Business name")} name="name" onFocus={onFocusHandler} />
      </Form.Item>

      <Form.Item
        name="url"
        style={{ padding: 0 }}
        label={t("Website URL")}
        rules={[
          {
            required: true,
            message: "Required fields were not filled."
          },
          {
            pattern: validateUrl,
            message: "Please enter valid business URL."
          }
        ]}
      >
        <InputComponent placeholder={t("Website URL")} name="url" onFocus={onFocusHandler} />
      </Form.Item>

      <Form.Item
        name="phone"
        style={{ padding: 0 }}
        label={
          <>
            {t("Phone number")} <Text type="secondary">(optional)</Text>
          </>
        }
        extra={
          isPhoneIncomplete
            ? t("This doesn't look like a full number yet. You can still continue.")
            : null
        }
      >
        <PhoneInput onFocus={onFocusHandler} />
      </Form.Item>

      {onboarding ? (
        <>
          <Form.Item noStyle>
            <Flex gap={4} style={{ margin: "24px 0px 8px 0px" }}>
              <Text style={{ fontWeight: 500 }}>
                {t("Are you currently using any other alert provider?")}
              </Text>
              <Tooltip
                placement="bottom"
                title={t(
                  "Select “Yes” if another alert provider is active on your account. You may need to unenroll or transfer before we can activate alerts."
                )}
                overlayInnerStyle={{ textAlign: "center" }}
                overlayStyle={{ minWidth: 275 }}
              >
                <Icon className="tooltip-icon" type={"circle_info"} />
              </Tooltip>
            </Flex>
          </Form.Item>

          <Form.Item
            name="is_use_another_solutions"
            style={{ padding: 0 }}
            rules={[
              {
                required: true,
                message: "Required fields were not filled."
              }
            ]}
          >
            <Radio.Group
              name="is_use_another_solutions"
              onChange={onFocusHandler}
              style={{ width: isMobile && "100%" }}
            >
              <Flex gap={12} vertical={isMobile}>
                {options.map((o) => (
                  <Button
                    key={o.value}
                    className={cn("choice-field", {
                      active: isUseAnotherSolutions === o.value
                    })}
                    onClick={(e) => {
                      handleClearError(e, "is_use_another_solutions")
                      form.setFieldValue("is_use_another_solutions", o.value)
                    }}
                  >
                    <Flex gap={8}>
                      <Text>{o.title}</Text>
                      <Text type="secondary">{o.description}</Text>
                    </Flex>
                  </Button>
                ))}
              </Flex>
            </Radio.Group>
          </Form.Item>
        </>
      ) : null}

      {!isMobile ? null : (
        <Button type="default" onClick={handleNavigate} size="large" style={{ width: "100%" }}>
          Save for later
        </Button>
      )}
      <ButtonComponent
        type="primary"
        size="large"
        htmlType="submit"
        className="btn-primary_animation"
        block
      >
        {t("Continue")}
      </ButtonComponent>
    </Form>
  )
}

export default BusinessInfoForm
